import React, { useState, useRef } from 'react';
import { View, Text, TouchableOpacity, StyleSheet } from 'react-native';
import MaterialIcons from 'react-native-vector-icons/MaterialIcons';
import Svg, { Path } from 'react-native-svg';
import TopOpportunitiesContainer from "../components/TopOpportunitiesContainer";
import TopOpportunitiesTable from "../components/TopOpportunitiesTable";
import TabSelector from '../components/TabSelector';
import { useStandUpDashboard } from '../hooks/useStandUpDashBoard';
import { useSelector } from 'react-redux';
import { RootState } from '../../../store';
import TopOpportunitiesBoardPrint, { TopOpportunitiesBoardPrintRef } from "../components/Print/TopOpportunitiesBoardPrint";
import TopOpportunitiesTablePrint, { TopOpportunitiesTablePrintRef } from "../components/Print/TopOpportunitiesTablePrint";

interface Props {
  onBackPress?: () => void;
  inProgressItems?: any[];
  completedItems?: any[];
  overdueItems?: any[];
  isLoadingTopOpportunities?: boolean;
  onViewActionPlan?: (id?: any) => void;
}

// Print Icon component
const PrintIcon = () => (
  <Svg width="16" height="16" viewBox="0 0 16 16" fill="none">
    <Path
      d="M12.6666 5.33333H11.9999V2H3.99992V5.33333H3.33325C2.22659 5.33333 1.33325 6.22667 1.33325 7.33333V11.3333H3.99992V14H11.9999V11.3333H14.6666V7.33333C14.6666 6.22667 13.7733 5.33333 12.6666 5.33333ZM5.33325 3.33333H10.6666V5.33333H5.33325V3.33333ZM10.6666 12.6667H5.33325V10H10.6666V12.6667ZM11.9999 10V8.66667H3.99992V10H2.66659V7.33333C2.66659 6.96667 2.96659 6.66667 3.33325 6.66667H12.6666C13.0333 6.66667 13.3333 6.96667 13.3333 7.33333V10H11.9999Z"
      fill="#034895"
    />
    <Path
      d="M11.9999 8.33333C12.3681 8.33333 12.6666 8.03486 12.6666 7.66667C12.6666 7.29848 12.3681 7 11.9999 7C11.6317 7 11.3333 7.29848 11.3333 7.66667C11.3333 8.03486 11.6317 8.33333 11.9999 8.33333Z"
      fill="#034895"
    />
  </Svg>
);

// Collapse Icon (arrows at center pointing outward to corners - to collapse/minimize the view)
const CollapseIcon = () => (
  <Svg width="32" height="32" viewBox="0 0 32 32" fill="none">
    <Path d="M4 0.5H28C29.933 0.5 31.5 2.067 31.5 4V28C31.5 29.933 29.933 31.5 28 31.5H4C2.067 31.5 0.5 29.933 0.5 28V4C0.5 2.067 2.067 0.5 4 0.5Z" fill="#FFFFFF" />
    <Path d="M4 0.5H28C29.933 0.5 31.5 2.067 31.5 4V28C31.5 29.933 29.933 31.5 28 31.5H4C2.067 31.5 0.5 29.933 0.5 28V4C0.5 2.067 2.067 0.5 4 0.5Z" stroke="#D8E7FB" />
    {/* Top-right arrow at center pointing to top-right corner */}
    <Path d="M17 15L17 11L18.5 11L18.5 12.8L22 9L23 10L19.2 13.8L21 13.8L21 15.3L17 15.3L17 15Z" fill="#1C1C1C" />
    {/* Bottom-left arrow at center pointing to bottom-left corner */}
    <Path d="M15 17L15 21L13.5 21L13.5 19.2L10 23L9 22L12.8 18.2L11 18.2L11 16.7L15 16.7L15 17Z" fill="#1C1C1C" />
  </Svg>
);

const StandUpTopOpportunitiesScreen: React.FC<Props> = ({
  onBackPress,
  inProgressItems: inProgressProp,
  completedItems: completedProp,
  overdueItems: overdueProp,
  isLoadingTopOpportunities: loadingProp,
  onViewActionPlan,
}) => {
  // Use the dashboard hook to get data and handlers (same as StandUpScreen)
  const {
    inProgressItems: hookInProgressItems,
    completedItems: hookCompletedItems,
    overdueItems: hookOverdueItems,
    isLoadingTopOpportunities: hookIsLoadingTopOpportunities,
    isTogglingFavorite,
    handleViewActionPlanDetails,
    handleToggleFavoriteOpportunity,
    pagination,
    currentPage,
    handlePageChange,
    handlePageSizeChange,
  } = useStandUpDashboard();

  // Local UI state to toggle between Board and Table views
  const [activeTab, setActiveTab] = useState<'board' | 'table'>('board');

  // Refs for print functionality
  const topOpportunitiesBoardRef = useRef<TopOpportunitiesBoardPrintRef | null>(null);
  const topOpportunitiesTableRef = useRef<TopOpportunitiesTablePrintRef | null>(null);

  const inProgressItems = inProgressProp ?? hookInProgressItems ?? [];
  const completedItems = completedProp ?? hookCompletedItems ?? [];
  const overdueItems = overdueProp ?? hookOverdueItems ?? [];
  const isLoadingTopOpportunities =
    typeof loadingProp === 'boolean' ? loadingProp : !!hookIsLoadingTopOpportunities;

  // Use Redux source of truth for the full table data (same as StandUpScreen)
  const topOpportunitiesTableData = useSelector((state: RootState) => state.standUp.topOpportunities);
  // Combined list fallback (not used for table when Redux data exists)
  const combinedTableItems = [...inProgressItems, ...completedItems, ...overdueItems];

  // Handle print based on active tab
  const handlePrint = () => {
    if (activeTab === 'board') {
      topOpportunitiesBoardRef.current?.print();
    } else {
      topOpportunitiesTableRef.current?.print();
    }
  };

  return (
    <View style={styles.container}>
      {/* Back link moved outside the white card */}
      <TouchableOpacity onPress={onBackPress} style={[styles.backWrapper, styles.backRow]} accessibilityLabel="Back to dashboard">
        <MaterialIcons name="arrow-back" size={20} color="#005CBC" style={styles.backIcon} />
        <Text style={styles.backText}>Back to dashboard</Text>
      </TouchableOpacity>

      <View style={styles.card}>
        {/* Header with title and action buttons */}
        <View style={styles.headerRowCard}>
          <Text style={styles.titleText}>Task tracker</Text>

          {/* Right side action buttons - Collapse and Print */}
          <View style={styles.headerRightActions}>
            <TouchableOpacity
              style={styles.collapseButton}
              onPress={onBackPress}
              accessibilityLabel="Collapse view"
            >
              <CollapseIcon />
            </TouchableOpacity>
            <TouchableOpacity
              style={styles.printButton}
              onPress={handlePrint}
              accessibilityLabel="Print"
            >
              <PrintIcon />
              <Text style={styles.printText}>Print</Text>
            </TouchableOpacity>
          </View>
        </View>

        {/* Centered Tabs - placed below the title to match StandUpScreen */}
        <View style={styles.tabsContainer}>
          <TabSelector activeTab={activeTab} onTabChange={setActiveTab} />
        </View>

        {/* Card body: either board columns or table */}
        {activeTab === 'board' ? (
          <View style={styles.cardBody}>
            <View style={styles.containersRow}>
              <TopOpportunitiesContainer
                title="In progress"
                count={inProgressItems?.length}
                items={inProgressItems}
                width="32.5%"
                height={490}
                onAddPress={onViewActionPlan}
                isLoading={isLoadingTopOpportunities}
              />

              <TopOpportunitiesContainer
                title="Done"
                count={completedItems?.length}
                items={completedItems}
                width="32.5%"
                height={490}
                onAddPress={onViewActionPlan}
                isLoading={isLoadingTopOpportunities}
              />

              <TopOpportunitiesContainer
                title="Overdue"
                count={overdueItems?.length}
                items={overdueItems}
                width="32.5%"
                height={490}
                onAddPress={onViewActionPlan}
                isLoading={isLoadingTopOpportunities}
              />
            </View>
          </View>
        ) : (
          <View style={[styles.cardBody, styles.tableView]}> 
            <TopOpportunitiesTable
              data={topOpportunitiesTableData && topOpportunitiesTableData.length ? topOpportunitiesTableData : combinedTableItems}
              isLoading={isLoadingTopOpportunities || !!isTogglingFavorite}
              onFavoriteToggle={handleToggleFavoriteOpportunity}
              onViewDetailsPress={handleViewActionPlanDetails}
              maxRecordsToShow={5}
              currentPage={currentPage}
              totalPages={pagination?.totalPages || 1}
              pageSize={pagination?.pageSize || 10}
              onPageChange={handlePageChange}
              onPageSizeChange={handlePageSizeChange}
            />
          </View>
        )}
      </View>

      {/* Render Print Components - hidden off-screen */}
      <View style={{ position: 'absolute', left: -9999, top: -9999 }}>
        {activeTab === 'board' ? (
          <TopOpportunitiesBoardPrint
            ref={topOpportunitiesBoardRef}
            inProgressItems={inProgressItems}
            completedItems={completedItems}
            overdueItems={overdueItems}
          />
        ) : (
          <TopOpportunitiesTablePrint
            ref={topOpportunitiesTableRef}
            data={topOpportunitiesTableData && topOpportunitiesTableData.length ? topOpportunitiesTableData : combinedTableItems}
            isLoading={isLoadingTopOpportunities || !!isTogglingFavorite}
            onFavoriteToggle={handleToggleFavoriteOpportunity}
            onViewDetailsPress={handleViewActionPlanDetails}
            maxRecordsToShow={5}
            currentPage={currentPage}
            totalPages={pagination?.totalPages || 1}
            pageSize={pagination?.pageSize || 10}
            onPageChange={handlePageChange}
            onPageSizeChange={handlePageSizeChange}
          />
        )}
      </View>
    </View>
  );
};

const styles = StyleSheet.create({
  container: {
    flex: 1,
    backgroundColor: '#F5F9FF',
    padding: 16,
  },
  backWrapper: {
    marginBottom: 24,
    marginTop: 24,
  },
  backRow: {
    flexDirection: 'row',
    alignItems: 'center',
  },
  backIcon: {
    marginRight: 8,
  },
  backWrapperInside: {
    marginBottom: 16,
  },
  backText: {
    color: '#034895',
    fontSize: 16,
    fontWeight: '700',
  },
  headerRow: {
    flexDirection: 'row',
    alignItems: 'center',
    justifyContent: 'space-between',
    marginBottom: 16,
  },
  headerRowCard: {
    flexDirection: 'row',
    alignItems: 'center',
    justifyContent: 'space-between',
    marginBottom: 24,
  },
  titleText: {
    fontSize: 24,
    fontWeight: '700',
    color: '#333333',
  },
  headerRightActions: {
    flexDirection: 'row',
    alignItems: 'center',
    gap: 12,
  },
  collapseButton: {
    flexShrink: 0,
  },
  printButton: {
    flexDirection: 'row',
    alignItems: 'center',
    gap: 6,
    paddingHorizontal: 12,
    paddingVertical: 6,
    borderColor: '#D8E7FB',
    borderWidth: 1,
    borderRadius: 4,
  },
  printText: {
    fontSize: 14,
    fontWeight: '400',
    color: '#034895',
  },
  headerCenter: {
    flex: 1,
    alignItems: 'center',
  },
  tabContainerCentered: {
    flexDirection: 'row',
    borderRadius: 6,
    backgroundColor: 'transparent',
    overflow: 'hidden',
  },
  tabButton: {
    paddingVertical: 8,
    paddingHorizontal: 16,
    backgroundColor: 'transparent',
  },
  activeTabButton: {
    backgroundColor: '#E9F2FF',
    borderRadius: 6,
  },
  tabText: {
    color: '#5A6B7A',
    fontSize: 14,
  },
  activeTabText: {
    color: '#005CBC',
    fontWeight: '600',
  },
  card: {
    backgroundColor: '#FFFFFF',
    borderRadius: 8,
    padding: 24,
    // Make it look like a centered card
    shadowColor: 'rgba(0, 0, 0, 0.08)',
    shadowOffset: { width: 0, height: 4 },
    shadowOpacity: 1,
    shadowRadius: 12,
    elevation: 6,
  },
  cardBody: {
    marginTop: 16,
  },
  containersRow: {
    flexDirection: 'row',
    gap: 12,
    justifyContent: 'space-between',
  },
  tableView: {
    backgroundColor: '#FFFFFF',
    borderRadius: 8,
    padding: 16,
    // elevation/shadow to match card feel
    shadowColor: 'rgba(0, 0, 0, 0.06)',
    shadowOffset: { width: 0, height: 2 },
    shadowOpacity: 1,
    shadowRadius: 8,
    elevation: 4,
  },
  tabsContainer: {
    alignItems: 'center',
    marginBottom: 8,
  },
});

export default StandUpTopOpportunitiesScreen;
